/**
 * In-memory token store — the SINGLE source of truth for the current user's
 * OAuth access + refresh tokens in the SPA runtime.
 *
 * Per spec §5.3.8, **access and refresh tokens are stored in-memory only**.
 * They are never persisted to `localStorage` / `sessionStorage` / cookies
 * because any such channel widens the XSS blast radius. The user profile
 * object — owned by `authSlice` — may persist to `localStorage` for
 * optimistic re-login UX, but the tokens themselves live only here.
 *
 * This module is intentionally framework-agnostic (no React, no Redux). The
 * axios request interceptor reads from it synchronously at request time,
 * which would be impossible if the store were a React context or a Redux
 * subscription. Keeping it a plain module also means a hard reload wipes
 * the tokens, matching the security model.
 *
 * SH-AUTH-005.
 */

let accessToken: string | null = null;
let refreshToken: string | null = null;
let expiresAt: number | null = null;

/**
 * Returns the current access token, or `null` when the user is signed out.
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Returns the current refresh token, or `null` when the user is signed out.
 */
export function getRefreshToken(): string | null {
  return refreshToken;
}

/**
 * Returns the epoch-millisecond timestamp at which the current access token
 * expires, or `null` when no token is loaded. Consumed by the session-expiry
 * warning UI (SF-AUTH-008) to drive a countdown.
 */
export function getExpiresAt(): number | null {
  return expiresAt;
}

/**
 * Parameters for {@link setTokens}. `expiresIn` is in **seconds**, matching
 * the OAuth spec and the `authorization.expires_in` field on the server
 * envelope.
 */
export interface SetTokensParams {
  accessToken: string;
  refreshToken: string;
  /** Lifetime of the access token in seconds (from the server envelope). */
  expiresIn: number;
}

/**
 * Replace the current tokens. Used by login, refresh, and heartbeat flows.
 * `expiresAt` is computed here so every caller reports the same clock.
 */
export function setTokens(params: SetTokensParams): void {
  accessToken = params.accessToken;
  refreshToken = params.refreshToken;
  expiresAt = Date.now() + params.expiresIn * 1000;
}

/**
 * Wipe the store. Invoked on logout, on refresh failure, and on any path
 * that invalidates the session. Safe to call from a signed-out state.
 */
export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  expiresAt = null;
}
